import { useEffect, useState } from "react"
import { Link } from "react-router-dom"
import { Cookie } from "lucide-react"
import { Button } from "@/components/ui/button"
import { analyticsEnabled, getConsent, setConsent } from "@/lib/marketing-analytics"

type ConsentLang = "en" | "sq" | "it"

const COPY: Record<ConsentLang, { title: string; body: string; policy: string; decline: string; accept: string }> = {
  en: {
    title: "We value your privacy",
    body: "We use cookies to measure our ads and improve the site. Nothing is tracked until you accept.",
    policy: "Privacy Policy",
    decline: "Decline",
    accept: "Accept",
  },
  sq: {
    title: "Privatësia jote ka rëndësi",
    body: "Përdorim cookies për të matur reklamat tona dhe për të përmirësuar faqen. Asgjë nuk gjurmohet pa pëlqimin tënd.",
    policy: "Politika e Privatësisë",
    decline: "Refuzo",
    accept: "Prano",
  },
  it: {
    title: "Rispettiamo la tua privacy",
    body: "Usiamo i cookie per misurare le nostre campagne e migliorare il sito. Nulla viene tracciato finché non accetti.",
    policy: "Informativa sulla Privacy",
    decline: "Rifiuta",
    accept: "Accetta",
  },
}

/** Bottom-of-page consent prompt for the marketing tags. Renders nothing once a choice is stored. */
export function CookieConsent({ lang = "en" }: { lang?: string }) {
  const [open, setOpen] = useState(false)
  const copy = COPY[(lang in COPY ? lang : "en") as ConsentLang]

  useEffect(() => {
    if (!analyticsEnabled() || getConsent()) return
    const timer = setTimeout(() => setOpen(true), 900)
    return () => clearTimeout(timer)
  }, [])

  if (!open) return null

  const choose = (value: "accepted" | "declined") => {
    setConsent(value)
    setOpen(false)
  }

  return (
    <div
      role="dialog"
      aria-live="polite"
      aria-label={copy.title}
      className="fixed inset-x-3 bottom-3 z-[70] mx-auto max-w-2xl animate-in fade-in slide-in-from-bottom-4 duration-500"
    >
      <div className="flex flex-col gap-4 rounded-2xl border bg-background/85 p-5 shadow-2xl backdrop-blur-xl sm:flex-row sm:items-center">
        <div className="flex flex-1 gap-3 text-sm leading-relaxed text-muted-foreground">
          <Cookie className="mt-0.5 h-5 w-5 flex-shrink-0 text-foreground" strokeWidth={1.75} />
          <p>
            <span className="block font-semibold text-foreground">{copy.title}</span>
            {copy.body}{" "}
            <Link to="/privacy" className="font-medium text-foreground underline underline-offset-2">
              {copy.policy}
            </Link>
          </p>
        </div>
        <div className="flex flex-shrink-0 gap-2">
          <Button variant="outline" onClick={() => choose("declined")}>{copy.decline}</Button>
          <Button onClick={() => choose("accepted")}>{copy.accept}</Button>
        </div>
      </div>
    </div>
  )
}
